import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Typography } from '../index';

export interface SplitFormLayoutProps {
  images: string[];
  children: ReactNode;
  leftHeader?: ReactNode;
  leftFooter?: ReactNode;
  mobile?: ReactNode;
  contentMaxWidth?: number | string;
  rightAspect?: string; // CSS aspect-ratio, e.g. '9 / 16'
  rightWidth?: string; // CSS width, e.g. 'max(5vw, calc(100vh * 9 / 16))'
  autoRotateMs?: number;
  currentImageIndex?: number; // controlled mode
  onImageIndexChange?: (index: number) => void; // controlled mode
  rightAltText?: string;
}

export default function SplitFormLayout(props: SplitFormLayoutProps): JSX.Element {
  const {
    images,
    children,
    leftHeader,
    leftFooter,
    mobile,
    contentMaxWidth = 500,
    rightAspect = '9 / 16',
    rightWidth = 'max(5vw, calc(100vh * 9 / 16))',
    autoRotateMs = 4000,
    currentImageIndex,
    onImageIndexChange,
    rightAltText = 'Carousel'
  } = props;

  const isControlled = typeof currentImageIndex === 'number' && typeof onImageIndexChange === 'function';
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0);
  const activeIndex = isControlled ? (currentImageIndex as number) : uncontrolledIndex;

  const hasImages = useMemo(() => Array.isArray(images) && images.length > 0, [images]);

  useEffect(() => {
    if (!hasImages || isControlled) return;
    const id = setInterval(() => {
      setUncontrolledIndex(prev => (prev + 1) % images.length);
    }, autoRotateMs);
    return () => clearInterval(id);
  }, [hasImages, images.length, autoRotateMs, isControlled]);

  const handleDotClick = (index: number) => {
    if (isControlled) {
      onImageIndexChange?.(index);
    } else {
      setUncontrolledIndex(index);
    }
  };

  return (
    <>
      {/* Mobile (xs-sm): render provided mobile node or fallback to children */}
      <Box data-testid="ui-layout-split-form-layout-container-action-1" sx={{ display: { xs: 'block', md: 'none' } }}>
        {mobile ?? (
          <Box data-testid="ui-layout-split-form-layout-container-action-2" sx={{
            height: '100vh',
            width: '100vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            p: 2
          }}>
            <Box data-testid="ui-layout-split-form-layout-container-action-3" sx={{
              width: '100%',
              maxWidth: contentMaxWidth,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <Box data-testid="ui-layout-split-form-layout-container-action-4" sx={{ p: 3 }}>
                {children}
              </Box>
              {leftFooter ? (
                <Box data-testid="ui-layout-split-form-layout-container-action-5" sx={{ borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                  {leftFooter}
                </Box>
              ) : null}
            </Box>
          </Box>
        )}
      </Box>

      {/* Desktop (md+): Split layout with left form and right image */}
      <Box data-testid="ui-layout-split-form-layout-container-action-6" sx={{
        height: '100vh',
        width: '100vw',
        display: { xs: 'none', md: 'flex' },
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Left side: form area with grey background */}
        <Box data-testid="ui-layout-split-form-layout-container-action-7" sx={{
          flex: '1 1 auto',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          p: 2
        }}>
          {/* White form container */}
          <Box data-testid="ui-layout-split-form-layout-container-action-8" sx={{
            width: '100%',
            maxWidth: `${contentMaxWidth}px`,
            height: '100%',
            backgroundColor: '#ffffff',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Sticky header slot */}
            <Box data-testid="ui-layout-split-form-layout-container-action-9" sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #e0e0e0',
              borderRadius: '12px 12px 0 0'
            }}>
              {leftHeader ?? (
                <Box data-testid="ui-layout-split-form-layout-container-action-10" sx={{ p: 1.5, minHeight: '64px', display: 'flex', alignItems: 'center' }}>
                  <Typography data-testid="ui-layout-split-form-layout-text-hub" variant="h6" sx={{ fontWeight: 600 }}>Hub</Typography>
                </Box>
              )}
            </Box>

            {/* Scrollable content area */}
            <Box data-testid="ui-layout-split-form-layout-container-action-11" sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto'
            }}>
              {children}
            </Box>

            {/* Footer slot */}
            {leftFooter ? (
              <Box data-testid="ui-layout-split-form-layout-container-action-12" sx={{ borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                {leftFooter}
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Right side: image area */}
        <Box data-testid="ui-layout-split-form-layout-container-action-13" sx={{
          flex: '0 0 auto',
          width: rightWidth,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          position: 'relative',
          height: '100vh',
          overflow: 'hidden'
        }}>
          <Box data-testid="ui-layout-split-form-layout-container-action-14" sx={{ width: '100%', aspectRatio: rightAspect }}>
            {hasImages ? (
              <Box data-testid="ui-layout-split-form-layout-container-action-15"
                component="img"
                src={images[activeIndex]}
                alt={rightAltText}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  display: 'block',
                  transition: 'opacity 0.5s ease-in-out'
                }}
              />
            ) : null}
          </Box>

          {/* Pagination dots */}
          {hasImages ? (
            <Box data-testid="ui-layout-split-form-layout-container-action-16" sx={{
              position: 'absolute',
              bottom: 30,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
              zIndex: 2
            }}>
              {images.map((_, index) => (
                <Box data-testid="ui-layout-split-form-layout-container-action-17"
                  key={index}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: index === activeIndex ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                  }}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
}


